// src/data/guidedPathRoutes.js
//
// SINGLE SOURCE OF TRUTH for where the dashboard sends a user.
// Both the scoring engine (src/utils/scoring.js, for weak-metric topic routes)
// and the Guided Path (src/components/dashboard/GuidedPath.jsx, for recovery +
// milestone nodes) read their destinations from here. Set it once → it applies
// to every user.
//
// IMPORTANT: every `route` below must resolve, or navigation silently falls
// back to the Dashboard (see AthletePortal.jsx default case). A route resolves
// if it is either:
//   • a top-level key in initialTopicContent (src/data/content.js), e.g.
//     'focus', 'sleep', 'self-discipline', 'nutrition-hydration', 'pain-injury',
//     'anxiety', 'depression', 'resilience', ... , OR
//   • an explicit AthletePortal case: 'dashboard', 'assessment', 'pulse',
//     'reset-recharge', 'brain-health', 'leadership',
//     'family-huddle', 'performance-123', 'kidzcorner', 'transition', OR
//   • a special alias AthletePortal maps: 'mental-health'→anxiety topic,
//     'mental-performance'→resilience topic.
// To re-point a destination, just change its `route` to one of the above.

// --- Weak-metric skill topics -------------------------------------------------
// Keyed by the internal metric id (as stored in latestStats / scoring perfKeys).
// `label` is the display name; `route` is the destination topic.
export const METRIC_ROUTES = {
    focus:        { label: 'Focus',           route: 'focus' },
    resilience:   { label: 'Resilience',      route: 'resilience' },
    motivation:   { label: 'Motivation',      route: 'motivation' },
    confidence:   { label: 'Confidence',      route: 'confidence' },
    discipline:   { label: 'Self Discipline', route: 'self-discipline' },
    awareness:    { label: 'Self Awareness',  route: 'self-awareness' },
    adaptability: { label: 'Adaptability',    route: 'adaptability' },
    sleep_perf:   { label: 'Sleep Quality',   route: 'sleep' },
    nutrition:    { label: 'Nutrition',       route: 'nutrition-hydration' },
    injury_mgmt:  { label: 'Injury Mgmt',     route: 'pain-injury' }
};

// --- Recovery-first nodes ------------------------------------------------------
// Shown at the TOP of the Guided Path when the user is Running Hot / At Risk /
// safety-flagged. `iconKey` is resolved to a lucide icon inside GuidedPath.
// Default: "Reset & Recharge" points at the dedicated Reset & Recharge hub
// ('reset-recharge') — change the routes here to re-curate.
export const RECOVERY_NODES = [
    {
        id: 'recovery-reset',
        title: 'Reset & Recharge',
        desc: 'Start with your inner state — gentle tools to ease the load before pushing performance.',
        iconKey: 'HeartHandshake',
        route: 'reset-recharge'
    },
    {
        id: 'recovery-sleep',
        title: 'Protect Your Sleep',
        desc: 'Rebuild your energy foundation. Recovery starts with how you sleep tonight.',
        iconKey: 'Moon',
        route: 'sleep'
    }
];

// --- Weekly milestone ----------------------------------------------------------
// The final path node. Which one is used depends on whether a pulse is due
// (handled in the component).
export const MILESTONE_ROUTES = {
    due: 'pulse',        // pulse overdue → take the quick pulse
    current: 'assessment' // already current → the full assessment/update
};
